import { Link } from "react-router-dom";
import { FlaskConical, Microscope, Beaker, FileText, ShieldCheck, Atom, ArrowRight } from "lucide-react";

export default function QualityPage() {
    return (
        <div data-testid="quality-page" className="pt-48 pb-24 bg-[var(--bg)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <header className="mb-16 max-w-3xl">
                    <div className="data-label text-brand mb-3">QUALITY CONTROL</div>
                    <h1 className="font-serif text-5xl md:text-6xl leading-tight mb-6">
                        Verifiable purity at <span className="italic text-brand">every step</span>.
                    </h1>
                    <p className="text-secondary leading-relaxed">
                        Aminoplex sources every compound through ISO-aligned partner laboratories with full chain-of-custody.
                        Each lot is verified by independent third-party analytical labs before it ever reaches a research desk.
                    </p>
                </header>

                {/* Process */}
                <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border)] border border-[var(--border)] mb-24">
                    {[
                        { icon: FlaskConical, n: "01", title: "Controlled Synthesis", body: "Solid-phase peptide synthesis under ISO-aligned protocols. Documented at every stage." },
                        { icon: Beaker, n: "02", title: "Multi-Stage Purification", body: "Crude product purified via preparative RP-HPLC; fractions analyzed for content and impurity." },
                        { icon: Microscope, n: "03", title: "Independent Analysis", body: "Final lot analyzed by an independent third-party laboratory: HPLC + ESI Mass Spectrometry." },
                        { icon: FileText, n: "04", title: "Certificate of Analysis", body: "Batch-specific COA generated with serial tracking, archived and made publicly verifiable." },
                    ].map((s) => {
                        const Icon = s.icon;
                        return (
                            <div key={s.n} className="bg-[var(--bg-carbon)] p-8 group hover:bg-[var(--bg-surface)] transition-colors">
                                <div className="flex items-baseline gap-4 mb-5">
                                    <span className="font-serif text-5xl text-brand opacity-30 group-hover:opacity-100 transition-opacity">{s.n}</span>
                                    <Icon size={18} className="text-brand opacity-50 group-hover:opacity-100 transition-opacity" />
                                </div>
                                <h3 className="font-serif text-xl mb-2">{s.title}</h3>
                                <p className="text-sm text-secondary leading-relaxed">{s.body}</p>
                            </div>
                        );
                    })}
                </section>

                {/* Testing detail */}
                <section className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-24">
                    <div className="lg:col-span-5">
                        <div className="data-label text-brand mb-3">ANALYTICAL METHODS</div>
                        <h2 className="font-serif text-4xl leading-tight mb-6">How we verify identity and purity.</h2>
                        <p className="text-secondary leading-relaxed">
                            We rely on two complementary analytical techniques to characterize every batch — one tells us
                            <span className="text-primary"> what</span> the compound is, the other tells us <span className="text-primary">how pure</span> it is.
                        </p>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <DetailCard title="HPLC" icon={Atom} bullets={[
                            "Reverse-phase chromatography",
                            "UV detection at 210 / 220 nm",
                            "Quantitative purity %",
                            "Resolves trace impurities",
                        ]} />
                        <DetailCard title="ESI Mass Spec" icon={Microscope} bullets={[
                            "Electrospray ionization",
                            "Confirms exact molecular weight",
                            "Identity verification",
                            "Detects sequence variants",
                        ]} />
                    </div>
                </section>

                {/* Standards */}
                <section className="bg-[var(--bg-carbon)] border border-[var(--border)] p-10 lg:p-14 mb-24">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        <div>
                            <ShieldCheck size={24} className="text-brand mb-4" />
                            <h3 className="font-serif text-2xl mb-2">≥99% Acceptance</h3>
                            <p className="text-sm text-secondary">Any lot failing the 99% chromatographic purity threshold is rejected and re-synthesized.</p>
                        </div>
                        <div>
                            <FileText size={24} className="text-brand mb-4" />
                            <h3 className="font-serif text-2xl mb-2">Batch-Specific COA</h3>
                            <p className="text-sm text-secondary">Every shipment includes the matching certificate. All COAs are archived in our public library.</p>
                        </div>
                        <div>
                            <Beaker size={24} className="text-brand mb-4" />
                            <h3 className="font-serif text-2xl mb-2">Cold-Chain Handling</h3>
                            <p className="text-sm text-secondary">Lyophilized products packed with desiccant; sensitive compounds shipped with insulated containers.</p>
                        </div>
                    </div>
                </section>

                <section className="text-center">
                    <h2 className="font-serif text-4xl mb-6">Ready to verify a batch?</h2>
                    <Link to="/coa" className="btn btn-gold">Open COA Library <ArrowRight size={14} /></Link>
                </section>
            </div>
        </div>
    );
}

function DetailCard({ title, icon: Icon, bullets }) {
    return (
        <div className="card p-6">
            <Icon size={18} className="text-brand mb-3" />
            <h4 className="font-serif text-xl mb-3">{title}</h4>
            <ul className="space-y-2">
                {bullets.map((b) => (
                    <li key={b} className="font-mono text-xs text-secondary flex items-center gap-2">
                        <span className="w-1 h-1 bg-brand" /> {b}
                    </li>
                ))}
            </ul>
        </div>
    );
}
